import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Building2, Users, Heart, Brain, TrendingUp, Clock, Award, Send, CheckCircle2, Loader2, Dumbbell, Percent } from 'lucide-react';
import { toast } from 'sonner';

const CorporateServices = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    numberOfEmployees: '',
    address: '',
    services: [],
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const benefits = [
    {
      icon: TrendingUp,
      title: "Increased Productivity",
      description: "Employees who exercise regularly show 15% higher productivity levels and better focus throughout the workday."
    },
    {
      icon: Heart,
      title: "Reduced Absenteeism",
      description: "Workplace wellness programs can reduce sick days by up to 27%, keeping your team healthy and present."
    },
    {
      icon: Brain,
      title: "Improved Mental Health",
      description: "Regular exercise significantly reduces stress, anxiety, and burnout among employees."
    },
    {
      icon: Users,
      title: "Better Team Morale",
      description: "Wellness initiatives boost employee satisfaction and create a positive, energised workplace culture."
    },
    {
      icon: Clock,
      title: "Reduced Healthcare Costs",
      description: "Companies with wellness programs see an average 25% reduction in health-related costs over time."
    },
    {
      icon: Award,
      title: "Talent Retention",
      description: "Employees are more likely to stay with companies that invest in their health and wellbeing."
    }
  ];

  const packageFeatures = [
    "Discounted personal training sessions for all employees",
    "Flexible scheduling around work hours",
    "On-site or local gym training options",
    "Customised fitness programs for each employee",
    "Regular progress tracking and reporting",
    "Nutrition guidance and support",
    "Group training sessions available",
    "Dedicated account manager for your business"
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/corporate-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          services: ['Corporate Personal Training Partnership']
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Partnership enquiry sent successfully!', {
          duration: 4000,
        });
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          numberOfEmployees: '',
          address: '',
          services: [],
          message: ''
        });
      } else {
        toast.error(data.detail || 'Failed to send enquiry. Please try WhatsApp instead.', {
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Corporate form error:', error);
      toast.error('Failed to send enquiry. Please try WhatsApp instead.', {
        duration: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="corporate" className="corporate-section">
      <div className="container mx-auto px-4">
        {/* Hero */}
        <div className="corporate-hero">
          <Building2 className="corporate-hero-icon" size={48} />
          <h2 className="section-title">Corporate Personal Training</h2>
          <div className="section-title-underline"></div>
          <p className="section-description">
            Partner with us to offer your employees professional personal training services at exclusive corporate rates
          </p>
        </div>

        {/* Main Offer Banner */}
        <div className="corporate-main-offer">
          <div className="offer-icon-wrapper">
            <Percent size={60} className="offer-icon" />
          </div>
          <div className="offer-content">
            <h3 className="offer-title">10% Off All Corporate Plans</h3>
            <p className="offer-subtitle">Exclusive discount for businesses looking to invest in their employees' health</p>
            <p className="offer-description">
              We partner with local businesses to provide discounted personal training services for their employees. 
              Whether your team works in Portsmouth, Southampton, or anywhere across Hampshire, we'll help your 
              employees get fit, stay healthy, and perform at their best.
            </p>
            <Button 
              onClick={() => document.getElementById('corporate-form').scrollIntoView({ behavior: 'smooth' })}
              className="offer-cta"
              size="lg"
            >
              <Dumbbell size={20} />
              Become a Partner
            </Button>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="corporate-benefits">
          <h3 className="corporate-subtitle">Why Invest in Employee Fitness?</h3>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <benefit.icon className="benefit-icon" size={32} />
                <h4 className="benefit-title">{benefit.title}</h4>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What's Included */}
        <div className="corporate-package">
          <h3 className="corporate-subtitle">What's Included in Your Partnership</h3>
          <div className="package-grid">
            {packageFeatures.map((feature, index) => (
              <div key={index} className="package-feature">
                <CheckCircle2 size={24} className="package-check" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Form */}
        <div id="corporate-form" className="corporate-form-section">
          <h3 className="corporate-subtitle">Start Your Corporate Partnership</h3>
          <p className="corporate-form-intro">
            Fill in the details below and I'll get back to you with a tailored partnership proposal for your organisation.
          </p>
          
          <form onSubmit={handleSubmit} className="corporate-form" data-testid="corporate-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="companyName">Company Name *</Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="Your company name"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  data-testid="corporate-company-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="contactName">Your Name *</Label>
                <Input
                  id="contactName"
                  name="contactName"
                  type="text"
                  placeholder="Contact person name"
                  value={formData.contactName}
                  onChange={handleChange}
                  required
                  data-testid="corporate-contact-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@company.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="corporate-email-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Contact number"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="corporate-phone-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="numberOfEmployees">Number of Employees *</Label>
                <select
                  id="numberOfEmployees"
                  name="numberOfEmployees"
                  value={formData.numberOfEmployees}
                  onChange={handleChange}
                  className="form-select"
                  required
                  data-testid="corporate-employees-select"
                >
                  <option value="">Select range</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-25">11-25 employees</option>
                  <option value="26-50">26-50 employees</option>
                  <option value="51-100">51-100 employees</option>
                  <option value="100+">100+ employees</option>
                </select>
              </div>
              <div className="form-group">
                <Label htmlFor="address">Company Location *</Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  placeholder="City/Area (e.g., Portsmouth)"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  data-testid="corporate-address-input"
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="message">Tell Us About Your Team</Label>
              <Textarea
                id="message"
                name="message"
                placeholder="How many employees might be interested? Any specific fitness goals or requirements?"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                data-testid="corporate-message-input"
              />
            </div>

            <Button 
              type="submit" 
              className="corporate-submit-btn"
              size="lg"
              disabled={isSubmitting}
              data-testid="corporate-submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Request Partnership Info
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default CorporateServices;
